/**
 * Pure data transformation functions for employer reports.
 * Extracted from Reports.tsx for testability.
 */

export interface PlanLookup {
  get(planId: number): { name?: string; carrier?: string; type?: string } | undefined;
}

function lookupPlanName(planMap: PlanLookup, planId: number): string {
  return planMap.get(planId)?.name || `Plan ${planId}`;
}

function lookupPlanCarrier(planMap: PlanLookup, planId: number): string {
  return planMap.get(planId)?.carrier || "-";
}

function formatCurrency(value: any): string {
  return `$${Number(value || 0).toFixed(2)}`;
}

export function transformEmployeeBenefitsDetail(
  enrollments: any[],
  employees: any[],
  dependents: any[],
  planMap: PlanLookup,
  formatDate: (d: string) => string,
) {
  return enrollments
    .filter((enr) => !enr.waivedCoverage && enr.status === "active")
    .map((enr) => {
      const emp = employees.find((e: any) => e.id === enr.employeeId);
      const empDeps = dependents.filter((d: any) => d.employeeId === enr.employeeId);
      return {
        employeeName: emp ? `${emp.firstName} ${emp.lastName}` : "-",
        planName: lookupPlanName(planMap, enr.planId),
        coverageTier: enr.coverageTier || "-",
        effectiveDate: enr.effectiveDate ? formatDate(enr.effectiveDate) : "-",
        employeePremium: formatCurrency(enr.employeePremium),
        employerContribution: formatCurrency(enr.employerPremium),
        dependents: empDeps.length,
      };
    });
}

export function transformPremiumBilling(
  enrollments: any[],
  employees: any[],
  planMap: PlanLookup,
  formatDate: (d: string) => string,
) {
  return enrollments
    .filter((enr) => !enr.waivedCoverage && enr.status === "active")
    .map((enr) => {
      const emp = employees.find((e: any) => e.id === enr.employeeId);
      return {
        employeeName: emp ? `${emp.firstName} ${emp.lastName}` : "-",
        planName: lookupPlanName(planMap, enr.planId),
        employeePremium: formatCurrency(enr.employeePremium),
        employerContribution: formatCurrency(enr.employerPremium),
        totalPremium: formatCurrency(enr.totalPremium),
        effectiveDate: enr.effectiveDate ? formatDate(enr.effectiveDate) : "-",
      };
    });
}

export function transformEmployerContributionSummary(
  enrollments: any[],
  planMap: PlanLookup,
) {
  const contribByPlan: Record<string, any> = {};
  enrollments
    .filter((enr) => !enr.waivedCoverage && !enr.dependentId && (enr.status === "active" || enr.status === "pending"))
    .forEach((enr) => {
      const name = lookupPlanName(planMap, enr.planId);
      const carrier = lookupPlanCarrier(planMap, enr.planId);
      const key = `${name}-${enr.coverageTier || "Unknown"}`;
      if (!contribByPlan[key]) {
        contribByPlan[key] = {
          planName: name,
          carrier,
          coverageTier: enr.coverageTier || "Unknown",
          enrolledCount: 0,
          totalEmployer: 0,
          totalPremium: 0,
        };
      }
      contribByPlan[key].enrolledCount++;
      contribByPlan[key].totalEmployer += Number(enr.employerPremium || 0);
      contribByPlan[key].totalPremium += Number(enr.totalPremium || 0);
    });
  return Object.values(contribByPlan).map((p: any) => ({
    planName: p.planName,
    carrier: p.carrier,
    coverageTier: p.coverageTier,
    enrolledCount: p.enrolledCount,
    employerContributionPerEmployee: `$${(p.totalEmployer / p.enrolledCount).toFixed(2)}`,
    totalEmployerContribution: `$${p.totalEmployer.toFixed(2)}`,
    totalPremium: `$${p.totalPremium.toFixed(2)}`,
    employerSharePct: p.totalPremium > 0 ? `${((p.totalEmployer / p.totalPremium) * 100).toFixed(0)}%` : "0%",
  }));
}
